/**
 * THE label-field rule.
 *
 * A record has to be representable by one line of text: in a relation chip, in a
 * search hit, in a picker, in a Slack message. Which field that is has to be decided
 * by a rule rather than per call site, because a relation chip and a search hit
 * showing different fields of the same record is the kind of inconsistency nobody
 * files a bug about and everybody distrusts.
 *
 * The rule, in order, first match wins:
 *  1. a field with `settings.isLabel === true` - the admin said so explicitly;
 *  2. a TEXT field named exactly `name` - the convention every object we seed follows;
 *  3. the first non-system TEXT field in creation order;
 *  4. nothing - callers then fall back to the record id.
 *
 * Only TEXT qualifies, deliberately. EMAIL and URL are textual too, but "the label of
 * this company is https://..." is not a label, it is a link, and PHONE stores a
 * composite. An object whose only textual field is an email can still opt in with the
 * explicit flag, which is what step 1 is for.
 *
 * Soft-deleted fields are never candidates; callers pass live fields only.
 */

export interface LabelCandidateField {
  readonly id: string;
  readonly name: string;
  /** `StoredFieldType`; a plain string so `shared` needs no import cycle here. */
  readonly type: string;
  readonly isSystem: boolean;
  readonly settings?: { readonly isLabel?: boolean | undefined } | undefined;
}

export const LABEL_FIELD_NAME_CONVENTION = 'name';

export function resolveLabelField<T extends LabelCandidateField>(
  fields: readonly T[],
): T | undefined {
  const flagged = fields.find(
    (field) => field.settings?.isLabel === true && field.type === 'TEXT' && !field.isSystem,
  );
  if (flagged !== undefined) return flagged;

  const conventional = fields.find(
    (field) =>
      field.name === LABEL_FIELD_NAME_CONVENTION && field.type === 'TEXT' && !field.isSystem,
  );
  if (conventional !== undefined) return conventional;

  return fields.find((field) => field.type === 'TEXT' && !field.isSystem);
}
